import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { sanitizeNoteInput, hasActualTextContent } from '../utils/sanitize';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { datetimeLocalToUTC } from '../utils/dateHelpers';
import DateTimePickerInput from './DateTimePickerInput';
import { useDateFormatter } from '../hooks/useDateFormatter';

interface StateTransitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedCase: any) => void;
  caseId: string;
  currentState: string;
  targetState: string;
  actionLabel: string;
}

interface FormData {
  // Awaiting Info fields
  infoRequired: string;
  contactEmails: string;
  
  // On Hold fields
  onHoldReason: string;
  communicateToCustomer: boolean;
  
  // Solution Proposed fields
  solution: string;
  
  // Closed fields
  resolvedById: string;
  closedById: string;
  resolvedAt: string;
  closedAt: string;
  resolutionCode: string;
  cause: string;
  resolutionNotes: string;
  addResolutionNotesToNotes: boolean;
  
  // Cancelled fields
  cancellationReason: string;
}

const RESOLUTION_CODE_OPTIONS = [
  { value: 'solved_permanently', label: 'Solved (Permanently)' },
  { value: 'solved_fixed_by_support', label: 'Solved – Fixed by Support/Guidance provided' },
  { value: 'solved_fixed_by_prb', label: 'Solved – Fixed by closing related PRB' },
  { value: 'solved_workaround_prb', label: 'Solved – Workaround provided based on open PRB' },
  { value: 'solved_by_customer', label: 'Solved by Customer' },
  { value: 'inconclusive_out_of_scope', label: 'Inconclusive – Out of scope' },
  { value: 'inconclusive_cannot_reproduce', label: 'Inconclusive – Cannot reproduce' },
  { value: 'inconclusive_no_workaround', label: 'Inconclusive – No workaround' },
  { value: 'duplicate_issue', label: 'Duplicate Issue' },
  { value: 'voided_canceled', label: 'Voided/Canceled' },
  { value: 'solved_fixed_by_change', label: 'Solved – Fixed by closing related Change Request' },
  { value: 'solved_fixed_by_incident', label: 'Solved – Fixed by closing related Incident' },
  { value: 'solved_related_request_closed', label: 'Solved – Related Request is Closed Complete' },
  { value: 'related_change_success_with_impact', label: 'Related Change Success with Impact' },
  { value: 'related_change_failed_with_impact', label: 'Related Change Failed with Impact' },
  { value: 'related_change_cancelled', label: 'Related Change Cancelled' },
  { value: 'related_change_postponed', label: 'Related Change Postponed' },
  { value: 'related_change_failed', label: 'Related Change Failed' },
  { value: 'auto_resolved', label: 'Auto Resolved' }
];

const StateTransitionModal: React.FC<StateTransitionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  caseId,
  currentState: _currentState,
  targetState,
  actionLabel
}) => {
  const { user: currentUser } = useAuth();
  const { preferences, formatDateTimeForInput } = useDateFormatter();

  const [formData, setFormData] = useState<FormData>({
    infoRequired: '',
    contactEmails: '',
    onHoldReason: '',
    communicateToCustomer: false,
    solution: '',
    resolvedById: '',
    closedById: '',
    resolvedAt: '',
    closedAt: '',
    resolutionCode: '',
    cause: '',
    resolutionNotes: '',
    addResolutionNotesToNotes: false,
    cancellationReason: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the current user's display name
  const currentUserDisplayName = currentUser
    ? (`${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || currentUser.email)
    : '';

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
    // Reset only when the modal opens or the target state changes — not on
    // currentUser identity churn, which would wipe notes while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentUser.id read inside resetForm
  }, [isOpen, targetState, currentUser?.id]);

  const resetForm = () => {
    setFormData({
      infoRequired: '',
      contactEmails: '',
      onHoldReason: '',
      communicateToCustomer: false,
      solution: '',
      resolvedById: currentUser?.id || '',
      closedById: currentUser?.id || '',
      // Leave blank so backend records server UTC time unless user explicitly picks a datetime.
      resolvedAt: '',
      closedAt: '',
      resolutionCode: '',
      cause: '',
      resolutionNotes: '',
      addResolutionNotesToNotes: false,
      cancellationReason: ''
    });
    setError(null);
  };

  const fillResolvedAtNow = () => {
    handleInputChange('resolvedAt', formatDateTimeForInput(new Date()));
  };

  const fillClosedAtNow = () => {
    handleInputChange('closedAt', formatDateTimeForInput(new Date()));
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateEmails = (emailString: string): string[] => {
    if (!emailString.trim()) return [];
    return emailString
      .split('\n')
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    switch (targetState) {
      case 'awaiting_info':
        if (!hasActualTextContent(formData.infoRequired)) {
          errors.push('Info Required field is mandatory');
        }
        if (formData.contactEmails.trim()) {
          const emails = validateEmails(formData.contactEmails);
          const invalidEmails = emails.filter(email => !isValidEmail(email));
          if (invalidEmails.length > 0) {
            errors.push(`Invalid email addresses: ${invalidEmails.join(', ')}`);
          }
        }
        break;

      case 'on_hold':
        if (!hasActualTextContent(formData.onHoldReason)) {
          errors.push('On-Hold Reason is mandatory');
        }
        break;

      case 'solution_proposed':
        if (!hasActualTextContent(formData.solution)) {
          errors.push('Solution field is mandatory');
        }
        break;

      case 'resolved':
        if (!hasActualTextContent(formData.resolutionNotes)) {
          errors.push('Resolution Notes are mandatory');
        }
        break;

      case 'closed':
        if (!formData.resolutionCode) {
          errors.push('Resolution Code is mandatory');
        }
        break;

      case 'cancelled':
        if (!hasActualTextContent(formData.cancellationReason)) {
          errors.push('Cancellation Reason is mandatory');
        }
        break;
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);

    try {
      // Prepare payload based on target state
      const payload: any = { newState: targetState };

      switch (targetState) {
        case 'awaiting_info':
          payload.infoRequired = sanitizeNoteInput(formData.infoRequired);
          if (formData.contactEmails.trim()) {
            payload.contactEmails = validateEmails(formData.contactEmails);
          }
          break;

        case 'on_hold':
          payload.onHoldReason = sanitizeNoteInput(formData.onHoldReason);
          break;

        case 'solution_proposed':
          payload.solution = sanitizeNoteInput(formData.solution);
          break;

        case 'resolved':
          payload.resolutionCode = formData.resolutionCode;
          payload.resolutionNotes = sanitizeNoteInput(formData.resolutionNotes);
          break;

        case 'closed':
          // Only send timestamps when the user explicitly picked a datetime.
          // Otherwise the backend uses server UTC (new Date()).
          if (formData.resolvedAt.trim()) {
            payload.resolvedAt = datetimeLocalToUTC(formData.resolvedAt, preferences.timezone);
          }
          if (formData.closedAt.trim()) {
            payload.closedAt = datetimeLocalToUTC(formData.closedAt, preferences.timezone);
          }
          payload.resolutionCode = formData.resolutionCode;
          payload.cause = sanitizeNoteInput(formData.cause);
          payload.resolutionNotes = sanitizeNoteInput(formData.resolutionNotes);
          // Also store on closureNotes when notes were entered so Closure tabs show them
          if (hasActualTextContent(formData.resolutionNotes)) {
            payload.closureNotes = sanitizeNoteInput(formData.resolutionNotes);
          }
          break;

        case 'cancelled':
          payload.cancellationReason = sanitizeNoteInput(formData.cancellationReason);
          break;
      }

      const response = await api.put(`/cases/${caseId}/state-transition`, payload);
      
      // Add resolution notes as work note if requested
      if (targetState === 'closed' && formData.addResolutionNotesToNotes && hasActualTextContent(formData.resolutionNotes)) {
        try {
          const sanitizedResolutionNotes = sanitizeNoteInput(formData.resolutionNotes);
          if (hasActualTextContent(sanitizedResolutionNotes)) {
            await api.post(`/cases/${caseId}/work-notes`, {
              note: sanitizedResolutionNotes,
              isPublic: false
            });
          }
        } catch (noteError) {
          console.warn('Failed to add resolution notes as work note:', noteError);
        }
      }

      onSuccess(response.data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to transition case state');
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    switch (targetState) {
      case 'awaiting_info':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Info Required <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={formData.infoRequired}
                onChange={(value) => handleInputChange('infoRequired', value)}
                placeholder="Describe what information is required..."
                minHeight="100px"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Send Info Request To (Email)
              </label>
              <textarea
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.contactEmails}
                onChange={(e) => handleInputChange('contactEmails', e.target.value)}
                placeholder="Enter email addresses, one per line..."
              />
              <p className="text-xs text-gray-500 mt-1">Email addresses to send the info request notification</p>
            </div>
          </>
        );

      case 'on_hold':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              On-Hold Reason <span className="text-red-500">*</span>
            </label>
            <RichTextEditor
              value={formData.onHoldReason}
              onChange={(value) => handleInputChange('onHoldReason', value)}
              placeholder="Explain why the case is being put on hold..."
              minHeight="100px"
            />
          </div>
        );

      case 'solution_proposed':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Solution <span className="text-red-500">*</span>
            </label>
            <RichTextEditor
              value={formData.solution}
              onChange={(value) => handleInputChange('solution', value)}
              placeholder="Describe the proposed solution..."
              minHeight="120px"
            />
          </div>
        );

      case 'resolved':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Code
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.resolutionCode}
                onChange={(e) => handleInputChange('resolutionCode', e.target.value)}
              >
                <option value="">Select resolution code...</option>
                {RESOLUTION_CODE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Notes <span className="text-red-500">*</span>
              </label>
              <RichTextEditor
                value={formData.resolutionNotes}
                onChange={(value) => handleInputChange('resolutionNotes', value)}
                placeholder="Describe how the incident was resolved..."
                minHeight="120px"
              />
            </div>
          </>
        );

      case 'closed':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resolved By
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed"
                  value={currentUserDisplayName}
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Closed By
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed"
                  value={currentUserDisplayName}
                  disabled
                  readOnly
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Resolved At
                </label>
                <DateTimePickerInput
                  value={formData.resolvedAt}
                  onChange={(value) => handleInputChange('resolvedAt', value)}
                  placeholder="Optional — server time if blank"
                />
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-500">
                    Leave blank to use current server time ({preferences.timezone}).
                  </p>
                  <button
                    type="button"
                    onClick={fillResolvedAtNow}
                    className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                  >
                    Use now
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Closed At
                </label>
                <DateTimePickerInput
                  value={formData.closedAt}
                  onChange={(value) => handleInputChange('closedAt', value)}
                  placeholder="Optional — server time if blank"
                />
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-500">
                    Leave blank to use current server time ({preferences.timezone}).
                  </p>
                  <button
                    type="button"
                    onClick={fillClosedAtNow}
                    className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                  >
                    Use now
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Code <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.resolutionCode}
                onChange={(e) => handleInputChange('resolutionCode', e.target.value)}
                required
              >
                <option value="">Select resolution code...</option>
                {RESOLUTION_CODE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cause
              </label>
              <RichTextEditor
                value={formData.cause}
                onChange={(value) => handleInputChange('cause', value)}
                placeholder="Describe the root cause..."
                minHeight="100px"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Resolution Notes
              </label>
              <RichTextEditor
                value={formData.resolutionNotes}
                onChange={(value) => handleInputChange('resolutionNotes', value)}
                placeholder="Describe the resolution..."
                minHeight="120px"
              />
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.addResolutionNotesToNotes}
                  onChange={(e) => handleInputChange('addResolutionNotesToNotes', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Add Resolution Notes to Notes</span>
              </label>
            </div>
          </>
        );

      case 'cancelled':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cancellation Reason <span className="text-red-500">*</span>
            </label>
            <RichTextEditor
              value={formData.cancellationReason}
              onChange={(value) => handleInputChange('cancellationReason', value)}
              placeholder="Explain why the case is being cancelled..."
              minHeight="100px"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                    {actionLabel}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start space-x-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {renderFormFields()}

                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : actionLabel}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default StateTransitionModal;